import { getSupabaseClient } from './supabase';

export interface PayWithRazorpayParams {
  tenantId: string;
  branchId?: string | null;
  purpose?: string;
  amount: number;
  referenceType: 'room_booking' | 'orders' | 'order' | 'sales_invoices' | 'sales_invoice' | string;
  referenceId: string;
  payerType?: 'consumer' | 'merchant_admin' | string;
  payerId?: string | null;
  prefillName?: string;
  prefillPhone?: string;
  prefillEmail?: string;
  onSuccess?: (rzpResponse: any, paymentOrderId?: string) => void;
  onFailure?: (error: any) => void;
}

export interface PayWithRazorpayResult {
  rzpResponse: any;
  paymentOrderId?: string;
}

/**
 * Dynamically loads the official Razorpay Checkout.js script if not already present.
 */
export function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }
    if ((window as any).Razorpay || document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      console.error('Failed to load Razorpay checkout script');
      resolve(false);
    };
    document.body.appendChild(script);
  });
}

/**
 * Universal Razorpay Checkout helper.
 * Calls generic Edge Function 'create-razorpay-order' with user's JWT
 * and opens the Razorpay modal.
 */
export async function payWithRazorpay(params: PayWithRazorpayParams): Promise<PayWithRazorpayResult> {
  const {
    tenantId,
    branchId,
    purpose = 'consumer_order',
    amount,
    referenceType,
    referenceId,
    payerType = 'consumer',
    payerId,
    prefillName = '',
    prefillPhone = '',
    prefillEmail = '',
    onSuccess,
    onFailure
  } = params;

  const notifyFailure = (err: any) => {
    try {
      if (onFailure) {
        onFailure(err);
      }
    } catch (e) {
      console.error('Error in onFailure callback:', e);
    }
  };

  const scriptLoaded = await loadRazorpayScript();
  if (!scriptLoaded) {
    const err = new Error('Payment gateway could not be loaded. Please check your internet connection.');
    notifyFailure(err);
    throw err;
  }

  const supabase = getSupabaseClient();
  if (!supabase) {
    const err = new Error('Database client not initialized');
    notifyFailure(err);
    throw err;
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;
  const token = session?.access_token;
  if (!token) {
    const err = new Error('User session not found. Please log in to complete payment.');
    notifyFailure(err);
    throw err;
  }

  // Normalize reference type to match backend expectations
  let normalizedRefType = referenceType;
  if (normalizedRefType === 'order') normalizedRefType = 'orders';
  if (normalizedRefType === 'sales_invoice') normalizedRefType = 'sales_invoices';

  const resolvedPayerId = payerId || session.user?.id || null;

  // Invoke generic Supabase Edge Function
  const { data: orderData, error: invokeError } = await supabase.functions.invoke('create-razorpay-order', {
    body: {
      tenant_id: tenantId,
      branch_id: branchId || null,
      purpose,
      amount: Number(amount),
      reference_type: normalizedRefType,
      reference_id: referenceId,
      payer_type: payerType,
      payer_id: resolvedPayerId
    },
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (invokeError) {
    console.error('Edge Function create-razorpay-order error:', invokeError);
    notifyFailure(invokeError);
    throw invokeError;
  }

  if (!orderData || orderData.error) {
    const errMsg = orderData?.error || 'Failed to initialize payment gateway order';
    const err = new Error(errMsg);
    notifyFailure(err);
    throw err;
  }

  const {
    razorpay_order_id,
    razorpay_key_id,
    amount: rzpAmount,
    currency = 'INR',
    payment_order_id
  } = orderData;

  return new Promise<PayWithRazorpayResult>((resolve, reject) => {
    let hasResolved = false;

    const options = {
      key: razorpay_key_id,
      amount: rzpAmount,
      currency,
      name: 'BahiBox',
      description: `Payment for ${normalizedRefType.replace('_', ' ')}`,
      order_id: razorpay_order_id,
      prefill: {
        name: prefillName,
        contact: prefillPhone,
        email: prefillEmail || session?.user?.email || ''
      },
      theme: {
        color: '#0f172a'
      },
      handler: function (rzpResponse: any) {
        if (hasResolved) return;
        hasResolved = true;
        try {
          if (onSuccess) {
            onSuccess(rzpResponse, payment_order_id);
          }
        } catch (e) {
          console.error('Error in onSuccess callback:', e);
        }
        resolve({ rzpResponse, paymentOrderId: payment_order_id });
      },
      modal: {
        ondismiss: function () {
          if (hasResolved) return;
          hasResolved = true;
          const dismissErr = new Error('Payment cancelled by user');
          notifyFailure(dismissErr);
          reject(dismissErr);
        }
      }
    };

    const rzp = new (window as any).Razorpay(options);
    rzp.on('payment.failed', function (resp: any) {
      if (hasResolved) return;
      hasResolved = true;
      const failErr = new Error(resp.error?.description || 'Payment failed on gateway');
      notifyFailure(failErr);
      reject(failErr);
    });

    rzp.open();
  });
}

/**
 * Helper to handle the race condition where the client Razorpay handler fires
 * before the Supabase webhook finishes writing `payment_status = 'paid'`.
 * Polls the specified table for `payment_status === targetStatus`.
 */
export async function pollPaymentStatus({
  table,
  id,
  targetStatus = 'paid',
  maxAttempts = 5,
  delayMs = 1500
}: {
  table: string;
  id: string;
  targetStatus?: string;
  maxAttempts?: number;
  delayMs?: number;
}): Promise<boolean> {
  const supabase = getSupabaseClient();
  if (!supabase) return false;

  for (let i = 0; i < maxAttempts; i++) {
    try {
      const { data } = await supabase
        .from(table)
        .select('payment_status')
        .eq('id', id)
        .maybeSingle();

      if (data?.payment_status === targetStatus) {
        return true;
      }
    } catch (err) {
      console.warn('Error polling payment status:', err);
    }
    if (i < maxAttempts - 1) {
      await new Promise((res) => setTimeout(res, delayMs));
    }
  }
  return false;
}
